<template>
  <div class="product-card-wrapper pt-0 px-0"> <!-- Elemento raíz único -->
    <!-- Product Card -->
    <div class="product-card">
      <div class="card">

        <!-- Product image -->
        <a :href="`/producto/${product.id}`" class="card-img-wrapper">
          <div class="image-container">
            <img 
              :src="product.image || require('@/assets/images/logo.png')" 
              :alt="product.name"
              class="card-img-top product-image p-0"
              :class="{'placeholder-image': !product.image}"
            >
            <div class="ver-producto-overlay">
              <span>Ver producto</span>
            </div>
            <div class="image-divider" aria-hidden="true">
              <span class="image-divider__line"></span>
            </div>
          </div>
        </a>

        <!-- Product info -->
        <div class="card-body card-body-product">
          <div class="product-info">
            <p
              v-if="displayBrand"
              class="product-brand"
            >
              {{ displayBrand }}
            </p>
            <div class="product-name-row">
              <span
                class="product-name-dot"
                aria-hidden="true"
              />
              <h3 class="product-name product-title">
                {{ product.name }}
              </h3>
            </div>
          </div>

          <div class="price-panel">
            <div class="price-panel__main">
              <div class="current-price product-price">
                <template v-if="priceRange.hasRange">
                  {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(priceRange.min) }}
                </template>
                <template v-else>
                  {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(finalPrice) }}
                </template>
              </div>

              <div class="price-panel__badges">
                <span
                  v-if="productDiscount > 0"
                  class="discount-chip"
                >
                  −{{ Math.round(productDiscount) }}%
                </span>
                <span
                  v-if="product.has_variants"
                  class="variant-badge"
                  title="Producto con variantes"
                  aria-label="Producto con variantes"
                >
                  <i class="fas fa-layer-group" aria-hidden="true"></i>
                  <span class="variant-badge__text">Variantes</span>
                </span>
              </div>
            </div>

            <div
              class="price-panel__was"
              :class="{ 'price-panel__was--hidden': productDiscount <= 0 }"
            >
              <span v-if="productDiscount > 0" class="was-label">Antes</span>
              <span v-if="productDiscount > 0" class="original-price">
                {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatPrice(productPrice) }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { defineProps, computed } from 'vue'
import { formatPrice } from "@/helpers/product";

const props = defineProps({
  product: {
    type: Object,
    required: true,
    default: () => ({
      id: null,
      name: '',
      category: '',
      subcategory: '',
      price: 0,
      stock: 0,
      description: '',
      features: [],
      brand: '',
      image: null,
      discount: 0,
      is_available: true
    })
  }
})

const displayBrand = computed(() => {
  const b = props.product.brand
  if (b === null || b === undefined) return ''
  const s = String(b).trim()
  return s
})

// Computed properties for product data — read directly from backend API response
const productPrice = computed(() => {
  const bp = props.product.base_price;
  if (bp !== undefined && bp !== null) return Number(bp);
  return Number(props.product.price || 0);
});

const productDiscount = computed(() => {
  const td = props.product.total_discount;
  if (td !== undefined && td !== null) return Number(td);
  const d = props.product.discount;
  if (d !== undefined && d !== null) return Number(d);
  return 0;
});

const finalPrice = computed(() => {
  if (props.product.effective_price !== undefined && props.product.effective_price !== null) {
    return Number(props.product.effective_price);
  }
  if (props.product.final_price !== undefined && props.product.final_price !== null) {
    return Number(props.product.final_price);
  }
  return productPrice.value;
});

// Price range for products with variants — uses effective_price (already includes discount)
const priceRange = computed(() => {
  if (!props.product.has_variants || !props.product.variants || !props.product.variants.length) {
    return { hasRange: false, min: finalPrice.value, max: finalPrice.value };
  }

  const prices = [];
  props.product.variants.forEach(variant => {
    if (variant.options && Array.isArray(variant.options)) {
      variant.options.forEach(option => {
        if (option.effective_price !== undefined && option.effective_price !== null) {
          prices.push(Number(option.effective_price));
        } else if (option.price !== undefined && option.price !== null) {
          prices.push(Number(option.price));
        }
      });
    }
  });

  if (prices.length === 0) {
    return { hasRange: false, min: finalPrice.value, max: finalPrice.value };
  }

  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return { hasRange: min !== max, min, max };
});
</script>

<style scoped>
.product-card-wrapper {
  width: 100%;
  margin: 0 auto;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.35);
}

.placeholder-image {
  object-fit: contain !important;
  background-color: #0f0f0f;
}

.product-card {
  background-color: #0b0b0b;
  overflow: hidden;
  box-shadow: 0 10px 26px rgba(0, 0, 0, 0.35);
  transition: transform var(--transition-base), box-shadow var(--transition-base);
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 12px;
}

.card {
  background-color: #0b0b0b;
  border: none;
}

.card-img-wrapper {
  display: block;
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.image-divider {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.image-divider__line {
  display: block;
  width: 62%;
  height: 2px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(0, 0, 0, 0) 0%,
    rgba(255, 255, 255, 0.0) 5%,
    var(--primary-color) 45%,
    var(--primary-color) 55%,
    rgba(255, 255, 255, 0.0) 95%,
    rgba(0, 0, 0, 0) 100%
  );
}

.image-container {
  position: relative;
  width: 100%;
  height: 300px;
  padding-top: 75%; /* Aspect ratio 4:3 */
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
}

.product-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.ver-producto-overlay {
  position: absolute;
  bottom: 10px;
  left: 10px;
  right: 10px;
  background-color: rgba(0, 0, 0, 0.65);
  color: #ffffff;
  text-align: center;
  padding: 10px 0;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.5px;
  border-radius: 4px;
  transform: translateY(calc(100% + 10px));
  opacity: 0;
  transition: transform 0.3s ease, opacity 0.3s ease;
  pointer-events: none;
}

.card-img-wrapper:hover .ver-producto-overlay {
  transform: translateY(0);
  opacity: 1;
}

.card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #0b0b0b;
}

.card-body-product {
  padding: 0;
  height: 260px;
  justify-content: space-between;
}

.product-info {
  padding: 14px 16px 10px;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.product-brand {
  margin: 0;
  padding: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #b5b5b5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-name-row {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;  
  min-height: 2.6em;
}

.product-name-dot {
  flex-shrink: 0;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: var(--primary-color);
}

.product-name {
  flex: 1;
  min-width: 0;
  font-size: 1.1875rem;
  line-height: 1.3;
  letter-spacing: -0.028em;
  margin: 0;
  padding: 0;
  color: #ffffff !important;
  font-weight: 600;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  overflow: hidden;
  text-wrap: balance;
}

.price-panel {
  flex-shrink: 0;
  padding: 12px 16px 14px;
  background: #0b0b0b;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  min-height: 120px;
}

.price-panel__main {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  min-height: 32px;
}

.price-panel__badges {
  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  min-height: 22px;
}

.original-price {
  display: inline-block;
  text-decoration: line-through;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
  color: #8b8b8b;
  font-size: 0.75rem;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.price-panel__was {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px dashed rgba(255, 255, 255, 0.12);
  min-height: 20px;
}

.price-panel__was--hidden {
  visibility: hidden;
  border-top-color: transparent;
}

.was-label {
  font-size: 0.625rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #b5b5b5;
}

.current-price {
  font-size: 1.25rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.035em;
  line-height: 1.08;
  color: #ffffff;
}

.discount-chip {
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  color: #fca5a5;
  background: rgba(239, 68, 68, 0.18);
  padding: 4px 8px;
  border-radius: var(--radius-sm);
  white-space: nowrap;
}

.variant-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: #cfcfcf;
  padding: 4px 9px;
  border-radius: var(--radius-full);
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.06);
}

.variant-badge__text {
  line-height: 1;
}

.variant-badge i {
  font-size: 0.75rem;
  opacity: 0.9;
}

.save-badge {
  background-color: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
  padding: 2px 6px;
  font-size: 12px;
  margin-left: 8px;
}

.card-actions {
  margin-top: auto;
}


@media (max-width: 767.98px) {
  .product-card-wrapper {
    max-width: 100%;
  }
  
  .card-body-product {
    min-height: 150px;
    height: auto;
    max-height: 150px;
  }
  
  .image-container {
    height: 250px;
  }
}

@media (max-width: 480px) {
  .product-card-wrapper {
    max-width: 100%;
  }
  
  .image-container {
    height: 220px;
  }
  
  .card-body-product {
    min-height: 180px;
  }

  .product-info {
    padding: 12px 12px 8px;
  }

  .price-panel {
    padding: 10px 12px 12px;
  }

  .product-brand {
    font-size: 0.625rem;
    letter-spacing: 0.06em;
  }

  .product-name-row {
    gap: 8px;
  }

  .product-name-dot {
    width: 5px;
    height: 5px;
  }

  .product-name {
    font-size: 1.0625rem;
    min-height: 2.55em;
  }

  .current-price {
    font-size: 1.25rem;
  }
}
</style>
